import { apiRequest, apiRequestBlob } from '../lib/httpClient'
import { ApiRequestError } from '../lib/httpClient'
import { normalizeFieldFormat } from '../lib/fieldFormat'
import type {
  FieldOption,
  NotificationListItem,
  NotificationListUpsertRequest,
  NotificationOption,
  NotificationOptionsUpsertRequest,
  OrganisationOption,
  Recipient,
  ReportBuilder,
  ReportBuilderDownload,
  ReportBuilderDownloadRequest,
  ReportBuilderDownloadResponse,
  ReportBuilderGroupItem,
  ReportBuilderGroupUpsertRequest,
  ReportBuilderListFilters,
  ReportBuilderListItem,
  ReportBuilderOptionsResponse,
  ReportBuilderOptionsUpsertRequest,
  ReportBuilderPreviewDataRequest,
  ReportBuilderPreviewDataResponse,
  ReportBuilderPreviewRequest,
  ReportBuilderScheduleListItem,
  ReportBuilderScheduleBulkCreateRequest,
  ReportBuilderScheduleUpsertRequest,
  ReportBuilderUpsertRequest,
  ReportRow,
  ReportStatus,
} from '../types/reports'

type Raw = Record<string, unknown>

function asString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown, fallback = 0) {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

function asBoolean(value: unknown, fallback = false) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return fallback
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function asStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((item) => asString(item)).filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

function mapFieldOption(raw: Raw): FieldOption {
  const type = asString(raw['type'] ?? raw['Type'])
  const drillLevel = asString(raw['drillLevel'] ?? raw['DrillLevel']).trim()
  const drillNext = asString(raw['drillNext'] ?? raw['DrillNext']).trim()
  return {
    value: asString(raw['value'] ?? raw['Value']),
    name: asString(raw['name'] ?? raw['Name']),
    type,
    isCustom: asBoolean(raw['isCustom'] ?? raw['IsCustom']),
    drillLevel: drillLevel || null,
    drillNext: drillNext || null,
    format: normalizeFieldFormat(raw['format'] ?? raw['Format'], type),
  }
}

function mapReportBuilderListItem(raw: Raw): ReportBuilderListItem {
  const recipients = asStringArray(raw['recipients'] ?? raw['Recipients'])
  return {
    reportBuilderId: asString(raw['reportBuilderId'] ?? raw['ReportBuilderId']),
    title: asString(raw['title'] ?? raw['Title']),
    description: asString(raw['description'] ?? raw['Description']),
    isTemplate: asBoolean(raw['isTemplate'] ?? raw['IsTemplate']),
    count: asNumber(raw['count'] ?? raw['Count']),
    recipients,
    platformType: asNumber(raw['platformType'] ?? raw['PlatformType']) || undefined,
    createdBy: asString(raw['createdBy'] ?? raw['CreatedBy']) || undefined,
    updatedAt:
      asString(raw['updatedAt'] ?? raw['UpdatedAt'] ?? raw['updatedOn'] ?? raw['UpdatedOn'] ?? raw['createdDate'] ?? raw['CreatedDate']) ||
      null,
  }
}

function mapReportBuilderScheduleItem(raw: Raw): ReportBuilderScheduleListItem {
  return {
    reportBuilderScheduleId: asString(raw['reportBuilderScheduleId'] ?? raw['ReportBuilderScheduleId']),
    reportBuilderId: asString(raw['reportBuilderId'] ?? raw['ReportBuilderId']),
    reportBuilderGroupId: asString(raw['reportBuilderGroupId'] ?? raw['ReportBuilderGroupId']) || undefined,
    title: asString(raw['title'] ?? raw['Title']),
    schedule: asString(raw['schedule'] ?? raw['Schedule']),
    time: asString(raw['time'] ?? raw['Time']),
    fileType: asString(raw['fileType'] ?? raw['FileType']),
    platformType: asNumber(raw['platformType'] ?? raw['PlatformType']) || undefined,
    displayTime: asString(raw['displayTime'] ?? raw['DisplayTime']) || undefined,
    companyName: asString(raw['companyName'] ?? raw['CompanyName']) || undefined,
    recipients: asArray<Raw>(raw['recipients'] ?? raw['Recipients']).map((item) => ({
      id: asString(item['id'] ?? item['Id']),
      type: asNumber(item['type'] ?? item['Type']),
    })),
    cc: asStringArray(raw['cc'] ?? raw['Cc']),
    subject: asString(raw['subject'] ?? raw['Subject']) || undefined,
    body: asString(raw['body'] ?? raw['Body']) || undefined,
  }
}

function mapNotificationOption(raw: Raw): NotificationOption {
  return {
    notificationOptionID: asString(raw['notificationOptionID'] ?? raw['NotificationOptionID']),
    name: asString(raw['name'] ?? raw['Name']),
    title: asString(raw['title'] ?? raw['Title']),
    defaultDAXQuery: asString(raw['defaultDAXQuery'] ?? raw['DefaultDAXQuery']),
    defaultEmailTemplate: asString(raw['defaultEmailTemplate'] ?? raw['DefaultEmailTemplate']),
    description: asString(raw['description'] ?? raw['Description']),
    platformType: asNumber(raw['platformType'] ?? raw['PlatformType']),
    parameters: asString(raw['parameters'] ?? raw['Parameters']),
    filteredToRecipients: asBoolean(raw['filteredToRecipients'] ?? raw['FilteredToRecipients']),
    createdBy: asString(raw['createdBy'] ?? raw['CreatedBy']) || undefined,
  }
}

function mapPlatformTypeOption(raw: unknown) {
  const rawObject = asObject(raw)
  const inferredValue = rawObject
    ? asNumber(
        rawObject['value'] ?? rawObject['Value'] ?? rawObject['platformType'] ?? rawObject['PlatformType'] ?? rawObject['id'] ?? rawObject['Id'],
        Number.NaN,
      )
    : asNumber(raw, Number.NaN)

  const fallbackLabel =
    inferredValue === 1
      ? 'Xero'
      : inferredValue === 2
        ? 'Xero XPM'
        : inferredValue === 3
          ? 'Xero WorkflowMax'
        : inferredValue === 4
          ? 'MYOB'
          : inferredValue === 5
            ? 'WorkGuru'
            : inferredValue === 6
              ? 'Bluerock Workflow Max'
              : inferredValue === 7
                ? 'Karbon'
                : inferredValue === 8
              ? 'FYI'
              : inferredValue === 9
                ? 'QuickBooks'
              : ''

  return {
    value: inferredValue,
    name: rawObject
      ? asString(rawObject['name'] ?? rawObject['Name'] ?? rawObject['label'] ?? rawObject['Label'] ?? rawObject['platformName'] ?? rawObject['PlatformName']) || fallbackLabel
      : fallbackLabel,
  }
}

function mapNotificationListItem(raw: Raw): NotificationListItem {
  return {
    notificationListID: asString(raw['notificationListID'] ?? raw['NotificationListID']),
    notificationOptionID: asString(raw['notificationOptionID'] ?? raw['NotificationOptionID']) || undefined,
    clientId: asString(raw['clientId'] ?? raw['ClientId']) || undefined,
    title: asString(raw['title'] ?? raw['Title']),
    schedule: asString(raw['schedule'] ?? raw['Schedule']),
    scheduleTime: asString(raw['scheduleTime'] ?? raw['ScheduleTime'] ?? raw['time'] ?? raw['Time']) || undefined,
    fileType: asString(raw['fileType'] ?? raw['FileType']) || undefined,
    companyId: asString(raw['companyId'] ?? raw['CompanyId']) || undefined,
    companyName: asString(raw['companyName'] ?? raw['CompanyName']) || undefined,
    cc: asStringArray(raw['cc'] ?? raw['Cc']),
    subject: asString(raw['subject'] ?? raw['Subject']) || undefined,
    body: asString(raw['body'] ?? raw['Body']) || undefined,
    recipients: asStringArray(raw['recipients'] ?? raw['Recipients']),
    createdBy: asString(raw['createdBy'] ?? raw['CreatedBy']),
  }
}

function mapRecipient(raw: Raw): Recipient {
  return {
    id: asString(raw['id'] ?? raw['Id']),
    name: asString(raw['name'] ?? raw['Name']),
    type: asNumber(raw['type'] ?? raw['Type']),
  }
}

function mapOrganisation(raw: Raw): OrganisationOption {
  return {
    companyId: asString(raw['companyId'] ?? raw['CompanyId']),
    companyName: asString(raw['companyName'] ?? raw['CompanyName']),
  }
}

function toReportStatus(row: ReportBuilderListItem): ReportStatus {
  if (row.count > 0) return 'Published'
  return row.isTemplate ? 'Draft' : 'Draft'
}

function toReportRow(row: ReportBuilderListItem): ReportRow {
  return {
    id: row.reportBuilderId,
    title: row.title,
    recipients: row.recipients.length,
    schedule: row.count > 0 ? `${row.count} scheduled` : 'None',
    status: toReportStatus(row),
    updatedAt: row.updatedAt || '',
  }
}

export async function fetchReportBuilderList(filters: ReportBuilderListFilters): Promise<ReportBuilderListItem[]> {
  if (!filters.clientId) return []

  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder/list', {
    method: 'GET',
    query: {
      clientId: filters.clientId,
      isTemplate: filters.isTemplate,
    },
  })

  let rows = asArray<Raw>(payload)
  if (!rows.length && payload && typeof payload === 'object') {
    const root = asObject(payload) ?? {}
    rows = asArray<Raw>(root['list'] ?? root['List'] ?? root['items'] ?? root['Items'])
  }

  let mapped = rows.map(mapReportBuilderListItem)
  if (filters.title?.trim()) {
    const query = filters.title.trim().toLowerCase()
    mapped = mapped.filter((row) => row.title.toLowerCase().includes(query))
  }

  return mapped
}

export async function fetchReportBuilderHomeList(params: { clientId: string; isTemplate?: boolean }): Promise<ReportBuilderGroupItem[]> {
  if (!params.clientId) return []
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder/init', {
    method: 'GET',
    query: {
      clientId: params.clientId,
      isTemplate: params.isTemplate,
    },
  })

  const rows = asArray<Raw>(payload)
  return rows.map((row) => ({
    reportBuilderGroupId: asString(row['reportBuilderGroupId'] ?? row['ReportBuilderGroupId']) || undefined,
    groupName: asString(row['groupName'] ?? row['GroupName']) || undefined,
    count: asNumber(row['count'] ?? row['Count']),
    platformType: asNumber(row['platformType'] ?? row['PlatformType']) || undefined,
    reportList: asArray<Raw>(row['reportList'] ?? row['ReportList']).map(mapReportBuilderListItem),
  }))
}

export async function fetchReportBuilder(reportBuilderId: string): Promise<ReportBuilder | null> {
  if (!reportBuilderId) return null
  const payload = await apiRequest<Raw>('notificationbuilder/ui/reportBuilder', {
    method: 'GET',
    query: { reportBuilderId },
  })

  if (!payload || Object.keys(payload).length === 0) return null

  return {
    reportBuilderId: asString(payload['reportBuilderId'] ?? payload['ReportBuilderId']),
    clientId: asString(payload['clientId'] ?? payload['ClientId']) || null,
    title: asString(payload['title'] ?? payload['Title']),
    description: asString(payload['description'] ?? payload['Description']),
    platformType: asNumber(payload['platformType'] ?? payload['PlatformType']),
    dimensions: asStringArray(payload['dimensions'] ?? payload['Dimensions']),
    measures: asStringArray(payload['measures'] ?? payload['Measures']),
    filters: asArray(payload['filters'] ?? payload['Filters']) as ReportBuilder['filters'],
    filteredToRecipients:
      asString(
        payload['filteredToRecipients'] ??
          payload['FilteredToRecipients'] ??
          payload['filteredByRecipients'] ??
          payload['FilteredByRecipients']
      ) ||
      null,
    isTemplate: asBoolean(payload['isTemplate'] ?? payload['IsTemplate']),
  }
}

export async function saveReportBuilder(payload: ReportBuilderUpsertRequest) {
  return apiRequest<string | null>('notificationbuilder/ui/reportBuilder/upsert', {
    method: 'POST',
    body: payload,
  })
}

function normalizeDeleteSuccess(payload: unknown): boolean {
  if (payload == null) return true
  if (typeof payload === 'boolean') return payload
  if (typeof payload === 'object') {
    const root = payload as Record<string, unknown>
    const success = root['success'] ?? root['Success']
    if (typeof success === 'boolean') return success
  }
  return true
}

export async function deleteReportBuilder(reportBuilderId: string) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder', {
    method: 'DELETE',
    query: { reportBuilderId },
  })
  return normalizeDeleteSuccess(payload)
}

export async function deleteReportBuilderList(reportBuilderIdList: string[]) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder/list', {
    method: 'DELETE',
    body: { reportBuilderIdList },
  })
  return normalizeDeleteSuccess(payload)
}

export async function fetchReportBuilderScheduleList(params: { id: string; clientId: string; isGroup?: boolean }) {
  if (!params.id || !params.clientId) return []
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilderSchedule/init/list', {
    method: 'GET',
    query: {
      id: params.id,
      clientId: params.clientId,
      isGroup: params.isGroup,
    },
  })

  return asArray<Record<string, unknown>>(payload).map(mapReportBuilderScheduleItem)
}

export async function fetchReportBuilderSchedule(reportBuilderScheduleId: string) {
  if (!reportBuilderScheduleId) return null
  return apiRequest<Record<string, unknown> | null>('notificationbuilder/ui/reportBuilderSchedule', {
    method: 'GET',
    query: { reportBuilderScheduleId },
  })
}

export async function saveReportBuilderSchedule(payload: ReportBuilderScheduleUpsertRequest) {
  return apiRequest<boolean>('notificationbuilder/ui/reportBuilderSchedule/upsert', {
    method: 'POST',
    body: payload,
  })
}

export async function bulkCreateReportBuilderSchedule(payload: ReportBuilderScheduleBulkCreateRequest) {
  return apiRequest<boolean>('notificationbuilder/ui/reportBuilderSchedule/bulk/create', {
    method: 'POST',
    body: payload,
  })
}

export async function deleteReportBuilderSchedule(reportBuilderScheduleId: string) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilderSchedule', {
    method: 'DELETE',
    query: { reportBuilderScheduleId },
  })
  return normalizeDeleteSuccess(payload)
}

export async function fetchReportBuilderOptions(params: { platformType: number; clientId: string }) {
  const payload = await apiRequest<Raw>('notificationbuilder/ui/reportBuilderOptions', {
    method: 'GET',
    query: params,
  })

  return {
    fieldDimensionOptions: asArray<Raw>(payload['fieldDimensionOptions']).map(mapFieldOption),
    fieldMeasureOptions: asArray<Raw>(payload['fieldMeasureOptions']).map(mapFieldOption),
    numberFilterOptions: asArray(payload['numberFilterOptions']) as ReportBuilderOptionsResponse['numberFilterOptions'],
    dateConditionFilterOptions: asArray(payload['dateConditionFilterOptions']) as ReportBuilderOptionsResponse['dateConditionFilterOptions'],
    dateFreqFilterOptions: asArray(payload['dateFreqFilterOptions']) as ReportBuilderOptionsResponse['dateFreqFilterOptions'],
    booleanFilterOptions: asArray(payload['booleanFilterOptions']) as ReportBuilderOptionsResponse['booleanFilterOptions'],
    booleanStringFilterOptions: asArray(payload['booleanStringFilterOptions']) as ReportBuilderOptionsResponse['booleanStringFilterOptions'],
    stringFilterOptions: asArray(payload['stringFilterOptions']) as ReportBuilderOptionsResponse['stringFilterOptions'],
  }
}

export async function saveReportBuilderOptions(payload: ReportBuilderOptionsUpsertRequest) {
  await apiRequest<void>('notificationbuilder/ui/reportBuilderOptions/upsert', {
    method: 'POST',
    body: payload,
  })
  return true
}

export async function fetchRecipients(clientId: string) {
  if (!clientId) return []
  const payload = await apiRequest<unknown>('notificationbuilder/ui/recipientList', {
    method: 'GET',
    query: { clientId },
  })

  return asArray<Raw>(payload).map(mapRecipient)
}

export async function fetchNotificationOptionsList(clientId?: string) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/notificationOptionsList', {
    method: 'GET',
    query: clientId ? { clientId } : undefined,
  })

  return asArray<Raw>(payload).map(mapNotificationOption)
}

export async function saveNotificationOptionsList(payload: NotificationOptionsUpsertRequest) {
  return apiRequest<boolean>('notificationbuilder/ui/notificationOptionsList/upsert', {
    method: 'POST',
    body: payload,
  })
}

export async function saveNotificationList(payload: NotificationListUpsertRequest) {
  return apiRequest<boolean>('notificationbuilder/ui/notificationList/upsert', {
    method: 'POST',
    body: payload,
  })
}

export async function saveReportBuilderGroup(payload: ReportBuilderGroupUpsertRequest) {
  try {
    return await apiRequest<boolean>('notificationbuilder/ui/reportBuilderGroup/upsert', {
      method: 'POST',
      body: payload,
    })
  } catch (error) {
    if (error instanceof ApiRequestError && (error.status === 404 || error.status === 405)) {
      return apiRequest<boolean>('notificationbuilder/ui/notificationList/upsert', {
        method: 'POST',
        body: payload,
      })
    }
    throw error
  }
}

export async function deleteNotificationOption(notificationOptionID: string) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/notificationOptionsList', {
    method: 'DELETE',
    query: { notificationOptionID },
  })
  return normalizeDeleteSuccess(payload)
}

export async function fetchNotificationList(params: { notificationOptionID: string; clientId: string }) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/notificationList', {
    method: 'GET',
    query: params,
  })

  return asArray<Raw>(payload).map(mapNotificationListItem)
}

export async function fetchNotificationOptionOrganisations(params: { clientId: string; platformType: number }) {
  const payload = await apiRequest<unknown>('notificationbuilder/ui/notificationOptions/organisations', {
    method: 'GET',
    query: params,
  })

  return asArray<Raw>(payload).map(mapOrganisation)
}

export async function updateNotificationSample(params: { notificationListID: string; clientId: string }) {
  return apiRequest<boolean>('notificationbuilder/ui/notificationList/updateSample', {
    method: 'POST',
    body: params,
  })
}

export async function getNotificationHomeList(clientId: string) {
  if (!clientId) return []
  return apiRequest<unknown[]>('notificationbuilder/ui/notificationList/notificationHomeList', {
    method: 'GET',
    query: { clientId },
  })
}

export async function getAvailablePlatformTypes(clientId: string) {
  if (!clientId) return []
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilderOptions/platformTypes', {
    method: 'GET',
    query: { clientId },
  })

  const payloadObject = asObject(payload)
  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payloadObject?.['data'])
      ? (payloadObject?.['data'] as Raw[])
      : Array.isArray(payloadObject?.['Data'])
        ? (payloadObject?.['Data'] as Raw[])
        : Array.isArray(payloadObject?.['result'])
          ? (payloadObject?.['result'] as Raw[])
          : Array.isArray(payloadObject?.['Result'])
            ? (payloadObject?.['Result'] as Raw[])
            : []

  return rawList
    .map(mapPlatformTypeOption)
    .filter((item) => Number.isFinite(item.value) && item.name)
}

export async function startReportBuilderPreview(payload: ReportBuilderPreviewRequest) {
  return apiRequest<string | null>('notificationbuilder/ui/reportBuilder/preview', {
    method: 'POST',
    body: payload,
  })
}

export async function fetchReportBuilderPreviewData(payload: ReportBuilderPreviewDataRequest) {
  return apiRequest<ReportBuilderPreviewDataResponse>('notificationbuilder/ui/reportBuilder/preview', {
    method: 'GET',
    query: payload,
  })
}

export async function startReportBuilderDownload(payload: ReportBuilderDownload) {
  return apiRequest<string | null>('notificationbuilder/ui/reportBuilder/download', {
    method: 'POST',
    body: payload,
  })
}

export async function fetchReportBuilderDownload(payload: ReportBuilderDownloadRequest) {
  return apiRequest<ReportBuilderDownloadResponse>('notificationbuilder/ui/reportBuilder/download', {
    method: 'GET',
    query: payload,
  })
}

export async function downloadReportBuilderFile(payload: ReportBuilderDownloadRequest) {
  return apiRequestBlob('notificationbuilder/ui/reportBuilder/download', {
    method: 'GET',
    query: payload,
  })
}

// Compatibility adapter for existing /reports page until route-family migration lands.
export async function fetchReports(filters: { title?: string; status?: string; clientId?: string } = {}) {
  if (!filters.clientId) return []
  const list = await fetchReportBuilderList({
    clientId: filters.clientId,
    isTemplate: false,
    title: filters.title,
  })

  let rows = list.map(toReportRow)
  if (filters.status && filters.status !== 'all') {
    rows = rows.filter((row) => row.status === filters.status)
  }

  return rows
}

export type {
  ReportStatus,
  ReportRow,
  ReportBuilder,
  ReportBuilderSchedule,
  ReportBuilderPreviewRequest,
  ReportBuilderDownload,
} from '../types/reports'
